import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from './Modal'

/**
 * @typedef {object} ExportItem
 * @property {string} id - Unieke waarde waarop de selectie matcht
 * @property {string} label - Wat de gebruiker ziet in de lijst
 * @property {string} [group] - De groep waar de regel bij hoort; alleen nodig bij `groups`
 */

/**
 * @typedef {object} ExportGroup
 * @property {string} id - Waarde die `ExportItem.group` draagt
 * @property {string} label - Wat de gebruiker ziet bij het vinkje
 */

/**
 * Venster om een export af te bakenen: alles of een selectie, en een periode.
 * Krijgt het `groups` mee, dan kiest de gebruiker daarnaast welke groepen
 * (bijv. statussen) meegaan; standaard staan ze allemaal aan.
 *
 * Het venster kiest alleen wát er geëxporteerd wordt; de aanroeper bepaalt hoe
 * de rijen eruitzien en start de download. Gebruikt via `common/Modal`, zoals
 * elk venster in deze app.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Of het venster open staat
 * @param {(open: boolean) => void} props.setModalOpen - Openen/sluiten
 * @param {string} props.pageHeader - Titel van het venster
 * @param {ExportItem[]} props.items - Waar de gebruiker uit kan kiezen
 * @param {string} props.itemsLabel - Kop boven de lijst, bijv. 'Deelnemers'
 * @param {(selection: {scope: 'all'|'selected', selectedIds: string[], startDate: Date|null, endDate: Date|null, format: 'xlsx'|'csv', groupIds?: string[]}) => void} props.onExport - Krijgt de keuze; `groupIds` alleen als er `groups` zijn.
 *   `format` gaat naar `downloaderFor` (Excel is de standaard: een csv landt in een Nederlandse Excel in één kolom)
 * @param {Date|null} [props.defaultStartDate] - Voorgevulde begindatum
 * @param {Date|null} [props.defaultEndDate] - Voorgevulde einddatum
 * @param {string} [props.emptyMessage] - Tekst wanneer er niets te kiezen valt
 * @param {ExportGroup[]} [props.groups] - Groepen om (uit) te vinken; weglaten = geen groepskeuze
 * @param {string} [props.groupsLabel] - Kop boven de groepen, bijv. 'Statussen'
 * @param {string} [props.groupNoun] - Eén groep in de waarschuwing, bijv. 'status'
 * @returns {JSX.Element}
 */
const ExportModal = ({
    modalOpen,
    setModalOpen,
    pageHeader,
    items = [],
    itemsLabel = 'Selectie',
    onExport,
    defaultStartDate = null,
    defaultEndDate = null,
    emptyMessage = 'Er is niets om te exporteren.',
    groups,
    groupsLabel = 'Groepen',
    groupNoun = 'groep',
}) => {
    const [scope, setScope] = useState('all')
    const [selectedIds, setSelectedIds] = useState([])
    const [groupIds, setGroupIds] = useState(/** @type {string[]} */ ([]))
    const hasGroups = Array.isArray(groups)
    // Welke groepen dit venster sinds het openen al heeft gezien. Een groep die
    // pas later binnenkomt (de huishoudens laadden nog) gaat aan; een groep die
    // de gebruiker uitvinkte blijft uit, ook als de lijst opnieuw wordt gebouwd.
    const seenGroupsRef = useRef(/** @type {Set<string>} */ (new Set()))
    const groupsKey = (groups || []).map((group) => group.id).join('|')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [format, setFormat] = useState(/** @type {'xlsx'|'csv'} */ ('xlsx'))

    // Op lokale datumdelen formatteren, niet via toISOString(): een datum die
    // met new Date(jaar, maand, dag) is gemaakt staat op lokale middernacht en
    // schuift in een zomerse tijdzone een dag terug bij omzetting naar UTC.
    const toInput = (date) => {
        if (!date) return ''
        const d = new Date(date)
        if (Number.isNaN(d.getTime())) return ''
        const maand = String(d.getMonth() + 1).padStart(2, '0')
        const dag = String(d.getDate()).padStart(2, '0')
        return `${d.getFullYear()}-${maand}-${dag}`
    }

    // Bij openen terug naar de beginstand, zodat een vorige keuze niet
    // blijft hangen in een volgende export.
    useEffect(() => {
        if (!modalOpen) return
        setScope('all')
        setSelectedIds([])
        const ids = (groups || []).map((group) => group.id)
        seenGroupsRef.current = new Set(ids)
        setGroupIds(ids)
        setFormat('xlsx')
        setStartDate(toInput(defaultStartDate))
        setEndDate(toInput(defaultEndDate))
        // Alleen bij openen: `groups` zelf staat er bewust niet in, want de
        // aanroeper bouwt die lijst bij elke snapshot opnieuw en dan zou een
        // uitgevinkte status vanzelf weer aan springen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen, defaultStartDate, defaultEndDate])

    useEffect(() => {
        if (!modalOpen) return
        const nieuw = (groups || [])
            .map((group) => group.id)
            .filter((id) => !seenGroupsRef.current.has(id))
        if (nieuw.length === 0) return
        nieuw.forEach((id) => seenGroupsRef.current.add(id))
        setGroupIds((prev) => [...prev, ...nieuw])
        // `groupsKey` en niet `groups`: die lijst krijgt elke snapshot een nieuwe identiteit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen, groupsKey])

    const toggleGroup = (id) => {
        setGroupIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        )
    }

    // Wat er na de groepskeuze nog in aanmerking komt. Zonder groepen: alles.
    const pickedGroups = useMemo(() => new Set(groupIds), [groupIds])
    const visibleItems = useMemo(
        () => (hasGroups ? items.filter((item) => pickedGroups.has(item.group ?? '')) : items),
        [hasGroups, items, pickedGroups]
    )
    const countByGroup = useMemo(() => {
        /** @type {Record<string, number>} */
        const counts = {}
        items.forEach((item) => {
            const key = item.group ?? ''
            counts[key] = (counts[key] || 0) + 1
        })
        return counts
    }, [items])

    const toggleId = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        )
    }

    // Een Set en geen `selectedIds.includes(...)` per regel: het
    // huishoudenoverzicht voert hier ~400 regels in, en dit venster hertekent
    // bij elke snapshot van de pagina. Dat waren tot 160.000 array-scans per
    // render.
    const selected = useMemo(() => new Set(selectedIds), [selectedIds])

    // Een vinkje bij een regel uit een groep die inmiddels uit staat telt niet
    // mee: die regel staat niet eens meer in de lijst.
    const visibleSelectedIds = useMemo(() => {
        const zichtbaar = new Set(visibleItems.map((item) => item.id))
        return selectedIds.filter((id) => zichtbaar.has(id))
    }, [selectedIds, visibleItems])

    const noGroupPicked = hasGroups && groupIds.length === 0
    const nothingPicked = scope === 'selected' && visibleSelectedIds.length === 0
    const periodInvalid = Boolean(startDate && endDate && startDate > endDate)

    const warning = useMemo(() => {
        if (periodInvalid) return 'De begindatum ligt na de einddatum.'
        if (noGroupPicked) return `Vink minstens één ${groupNoun} aan.`
        if (nothingPicked) return 'Vink minstens één regel aan, of kies alles.'
        return ''
    }, [periodInvalid, noGroupPicked, nothingPicked, groupNoun])

    const exportHandler = () => {
        if (warning) return
        onExport({
            scope,
            selectedIds: visibleSelectedIds,
            startDate: startDate ? new Date(startDate) : null,
            endDate: endDate ? new Date(endDate) : null,
            // De volgorde van de groepen, niet die van het klikken.
            ...(hasGroups ? { groupIds: groups.map((group) => group.id).filter((id) => pickedGroups.has(id)) } : {}),
            format,
        })
        setModalOpen(false)
    }

    const content = (
        <div className="export-modal">
            {hasGroups && (
                <div className="export-modal-section">
                    <p className="export-modal-label"><b>{groupsLabel}</b></p>
                    {groups.map((group) => (
                        <label key={group.id} className="export-modal-item">
                            <input
                                type="checkbox"
                                checked={pickedGroups.has(group.id)}
                                onChange={() => toggleGroup(group.id)}
                            />
                            <span>{`${group.label} (${countByGroup[group.id] || 0})`}</span>
                        </label>
                    ))}
                </div>
            )}

            <div className="export-modal-section">
                <p className="export-modal-label"><b>{itemsLabel}</b></p>

                <label className="export-modal-radio">
                    <input
                        type="radio"
                        name="export-scope"
                        checked={scope === 'all'}
                        onChange={() => setScope('all')}
                    />
                    <span>Alles ({visibleItems.length})</span>
                </label>

                <label className="export-modal-radio">
                    <input
                        type="radio"
                        name="export-scope"
                        checked={scope === 'selected'}
                        onChange={() => setScope('selected')}
                    />
                    <span>Een selectie</span>
                </label>

                {scope === 'selected' && (
                    <div className="export-modal-items">
                        {visibleItems.length === 0 ? (
                            <p>{emptyMessage}</p>
                        ) : (
                            visibleItems.map((item) => (
                                <label key={item.id} className="export-modal-item">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(item.id)}
                                        onChange={() => toggleId(item.id)}
                                    />
                                    <span>{item.label}</span>
                                </label>
                            ))
                        )}
                    </div>
                )}
            </div>

            <div className="export-modal-section">
                <p className="export-modal-label"><b>Periode</b></p>
                <div className="export-modal-dates">
                    <label>
                        <span>Vanaf</span>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </label>
                    <label>
                        <span>T/m</span>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </label>
                </div>
                <p className="export-modal-hint">Laat een datum leeg om die grens weg te laten.</p>
            </div>

            <div className="export-modal-section">
                <p className="export-modal-label"><b>Bestandsformaat</b></p>
                <label className="export-modal-radio">
                    <input
                        type="radio"
                        name="export-format"
                        checked={format === 'xlsx'}
                        onChange={() => setFormat('xlsx')}
                    />
                    <span>Excel (.xlsx)</span>
                </label>
                <label className="export-modal-radio">
                    <input
                        type="radio"
                        name="export-format"
                        checked={format === 'csv'}
                        onChange={() => setFormat('csv')}
                    />
                    <span>CSV</span>
                </label>
            </div>

            {warning && <p className="export-modal-warning" role="alert">{warning}</p>}
        </div>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader={pageHeader}
            confirm={exportHandler}
            confirmDisabled={Boolean(warning)}
            confirmLabel="Exporteren"
            content={content}
        />
    )
}

export default ExportModal
